import { apiClient } from "@api/client";
import { request } from "@api/request";
import type { components } from "@api/schema";

export interface CaptureSegment {
  kind: "flowing" | "gap";
  start: string;
  end: string;
}

export interface VisitPresence {
  visitUuid: string;
  patientName: string | null;
  enteredAt: string;
  /** Null while the patient is still in the room (open-ended stay). */
  leftAt: string | null;
}

export interface RoomCaptureTimeline {
  roomUuid: string;
  roomName: string;
  segments: CaptureSegment[];
  /**
   * Presence intervals overlapping the day at all, un-clamped — start/end
   * may fall outside the response window; rendering clamps.
   */
  stays: VisitPresence[];
}

export interface CaptureTimeline {
  windowStart: string;
  windowEnd: string;
  rooms: RoomCaptureTimeline[];
}

export type CaptureVerdict =
  components["schemas"]["FlaggedVisitOut"]["verdict"];

export interface EvidenceStay {
  roomUuid: string;
  enteredAt: string;
  leftAt: string | null;
}

export interface EvidenceTranscript {
  transcriptUuid: string;
  roomUuid: string | null;
  startedAt: string;
  lastTurnAt: string | null;
  turnCount: number;
  subjectLinked: boolean;
  roomOverlap: boolean;
  needsRecovery: boolean;
}

export interface CaptureEvidence {
  windowStart: string | null;
  windowEnd: string | null;
  stays: EvidenceStay[];
  transcripts: EvidenceTranscript[];
  chunkMessageCount: number;
  memoCount: number;
  factCount: number;
  hansDone: number;
  hansFailed: number;
  hansPending: number;
  stagesComplete: boolean | null;
}

export interface FlaggedVisit {
  visitUuid: string;
  patientUuid: string;
  patientName: string | null;
  visitType: string;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  verdict: CaptureVerdict;
  confidence: number;
  reasoning: string;
  evidence: CaptureEvidence;
  judgedAt: string;
}

const toCaptureSegment = (
  s: components["schemas"]["TimelineSegmentOut"],
): CaptureSegment => ({
  kind: s.kind,
  start: s.start,
  end: s.end,
});

const toVisitPresence = (
  s: components["schemas"]["VisitPresenceOut"],
): VisitPresence => ({
  visitUuid: s.visit_uuid,
  patientName: s.patient_name,
  enteredAt: s.entered_at,
  leftAt: s.left_at,
});

const toRoomCaptureTimeline = (
  r: components["schemas"]["RoomCaptureTimelineOut"],
): RoomCaptureTimeline => ({
  roomUuid: r.room_uuid,
  roomName: r.room_name,
  segments: r.segments.map(toCaptureSegment),
  stays: r.stays.map(toVisitPresence),
});

const toCaptureTimeline = (
  t: components["schemas"]["CaptureTimelineOut"],
): CaptureTimeline => ({
  windowStart: t.window_start,
  windowEnd: t.window_end,
  rooms: t.rooms.map(toRoomCaptureTimeline),
});

const toEvidenceStay = (
  s: components["schemas"]["StayEvidence"],
): EvidenceStay => ({
  roomUuid: s.room_uuid,
  enteredAt: s.entered_at,
  leftAt: s.left_at,
});

const toEvidenceTranscript = (
  t: components["schemas"]["TranscriptEvidence"],
): EvidenceTranscript => ({
  transcriptUuid: t.transcript_uuid,
  roomUuid: t.room_uuid,
  startedAt: t.started_at,
  lastTurnAt: t.last_turn_at,
  turnCount: t.turn_count,
  subjectLinked: t.subject_linked,
  roomOverlap: t.room_overlap,
  needsRecovery: t.needs_recovery,
});

const toCaptureEvidence = (
  e: components["schemas"]["VisitEvidence"],
): CaptureEvidence => ({
  windowStart: e.window_start,
  windowEnd: e.window_end,
  stays: e.stays.map(toEvidenceStay),
  transcripts: e.transcripts.map(toEvidenceTranscript),
  chunkMessageCount: e.chunk_message_count,
  memoCount: e.memo_count,
  factCount: e.fact_count,
  hansDone: e.hans_done,
  hansFailed: e.hans_failed,
  hansPending: e.hans_pending,
  stagesComplete: e.stages_complete,
});

export const toFlaggedVisit = (
  v: components["schemas"]["FlaggedVisitOut"],
): FlaggedVisit => ({
  visitUuid: v.visit_uuid,
  patientUuid: v.patient_uuid,
  patientName: v.patient_name,
  visitType: v.visit_type,
  scheduledStart: v.scheduled_start,
  scheduledEnd: v.scheduled_end,
  verdict: v.verdict,
  confidence: v.confidence,
  reasoning: v.reasoning,
  evidence: toCaptureEvidence(v.evidence),
  judgedAt: v.judged_at,
});

/**
 * One capture node (a Mike/Cameron device) as last reported by the clinic's
 * Zuul. `online`/`recording` are the node's self-reported state; `connectorLive`
 * is whether Zuul's link to Brian is up right now — when false the row may be
 * stale (outdated) rather than reflecting the node's true state, so the UI
 * shows it as unknown, not offline.
 */
export interface CaptureNode {
  uuid: string;
  instanceId: string;
  roomUuid: string | null;
  roomName: string;
  online: boolean;
  recording: boolean;
  lastSeen: string;
  reportedAt: string;
  connectorUuid: string;
  connectorLive: boolean;
}

const toCaptureNode = (
  n: components["schemas"]["CaptureNodeOut"],
): CaptureNode => ({
  uuid: n.uuid,
  instanceId: n.instance_id,
  roomUuid: n.room_uuid,
  roomName: n.room_name,
  online: n.online,
  recording: n.recording,
  lastSeen: n.last_seen,
  reportedAt: n.reported_at,
  connectorUuid: n.connector_uuid,
  connectorLive: n.connector_live,
});

export const captureClient = {
  /**
   * The clinic-local calendar day's room-by-room capture timeline. `date`
   * defaults server-side to the clinic's current day when omitted.
   */
  timeline: (date?: string): Promise<CaptureTimeline> =>
    request(() =>
      apiClient.GET("/rooms/capture-timeline", {
        params: { query: date ? { date } : {} },
      }),
    ).then(toCaptureTimeline),

  flagged: (limit = 100): Promise<FlaggedVisit[]> =>
    request(() =>
      apiClient.GET("/capture-verdicts/flagged", {
        params: { query: { limit } },
      }),
    ).then((rows) => rows.map(toFlaggedVisit)),

  /** The current capture-node roster, as last reported by the clinic's Zuul. */
  nodes: (): Promise<CaptureNode[]> =>
    request(() => apiClient.GET("/capture-nodes")).then((rows) =>
      rows.map(toCaptureNode),
    ),
};
